import { useEffect, useState } from "react";
import api, { formatApiError } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetTrigger, SheetFooter, SheetDescription } from "@/components/ui/sheet";
import { Plus, Pencil, Trash2, ShieldAlert, IdCard } from "lucide-react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { useAuth } from "@/contexts/AuthContext";
import PdiPermissionSettings from "@/components/PdiPermissionSettings";
import HrUserImport from "@/components/HrUserImport";

// HR module access, same rule as the sidebar (navConfig canSeeHr).
const hrAccess = (u) => {
  const perms = u?.module_permissions || [];
  if (u?.role === "admin") return "Yes — admin";
  if (!perms.length) return "Yes — role defaults";
  return perms.includes("hr") ? "Yes — granted" : "No";
};

// Imported from the HR app: signs in with employee code + HR password
// (backend hr_user_import.uses_hr_password).
const usesHrPassword = (u) => !!u?.hr_emp_code && (u.auth === "hr" || u.source === "HR app import");

const ROLE_CLS = {
  admin: "bg-purple-50 text-purple-700",
  production_manager: "bg-blue-50 text-blue-700",
  quality_inspector: "bg-emerald-50 text-emerald-700",
  store_manager: "bg-indigo-50 text-indigo-700",
  sales_crm: "bg-rose-50 text-rose-700",
  planner: "bg-blue-50 text-blue-700",
  operator: "bg-amber-50 text-amber-700",
  quality: "bg-emerald-50 text-emerald-700",
  stores: "bg-indigo-50 text-indigo-700",
  routing: "bg-rose-50 text-rose-700",
};

export default function UsersPage() {
  const { user } = useAuth();
  const [list, setList] = useState([]);
  const [roles, setRoles] = useState([]);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState({ email: "", name: "", role: "production_manager", password: "", designation: "", phone: "", signature_url: "", employee_code: "", logbook_user_id: "" });
  const [busy, setBusy] = useState(false);
  // HR → Logbook accounts (backend/hr_logbook.py); null while Logbook can't be reached.
  const [lbAccounts, setLbAccounts] = useState(null);

  const isAdmin = user?.role === "admin";
  // HR manages accounts too, but never admins, roles of existing users,
  // passwords of existing users, deletion or module permissions (the
  // backend enforces the same rules).
  const isHr = user?.role === "hr";
  const canManage = isAdmin || isHr;
  const roleOptions = isAdmin ? roles : roles.filter((r) => r.code !== "admin");

  const load = async () => {
    const [u, r] = await Promise.all([api.get("/users"), api.get("/users/roles")]);
    setList(u.data);
    setRoles(r.data);
    api.get("/hr/logbook/accounts").then(({ data }) => setLbAccounts(data)).catch(() => setLbAccounts(null));
  };

  useEffect(() => { if (canManage) load(); }, [canManage]);

  if (!canManage) {
    return (
      <div className="max-w-md mx-auto mt-12 bg-white border border-slate-200 rounded-lg p-6 text-center" data-testid="users-403">
        <ShieldAlert className="w-10 h-10 text-amber-500 mx-auto" />
        <h2 className="text-xl font-bold mt-3 text-slate-800" style={{ fontFamily: "Manrope" }}>Admins only</h2>
        <p className="text-sm text-slate-500 mt-1">User management is restricted to administrators. Ask your admin to invite you.</p>
      </div>
    );
  }

  const startCreate = () => { setEditing(null); setForm({ email: "", name: "", role: "production_manager", password: "", designation: "", phone: "", signature_url: "", employee_code: "", logbook_user_id: "" }); setOpen(true); };
  const startEdit = (u) => { setEditing(u.id); setForm({ email: u.email, name: u.name, role: u.role, password: "", designation: u.designation || "", phone: u.phone || "", signature_url: u.signature_url || "", employee_code: u.employee_code || "", logbook_user_id: u.logbook_user_id || "" }); setOpen(true); };
  const editingUser = list.find((x) => x.id === editing);

  // Logbook account link (HR → Logbook) — saved separately, only when changed.
  const saveLogbookLink = async (uid, before) => {
    if (lbAccounts === null || (form.logbook_user_id || "") === (before || "")) return;
    await api.put(`/hr/logbook/links/${uid}`, { logbook_user_id: form.logbook_user_id || null });
  };

  const save = async () => {
    if (busy) return;
    setBusy(true);
    try {
      if (editing) {
        const payload = { name: form.name, designation: form.designation, phone: form.phone, signature_url: form.signature_url, employee_code: form.employee_code };
        if (isAdmin) payload.role = form.role;
        if (isAdmin && form.password) payload.password = form.password;
        await api.patch(`/users/${editing}`, payload);
        await saveLogbookLink(editing, editingUser?.logbook_user_id);
        toast.success("User updated");
      } else {
        if (!form.password || form.password.length < 6) return toast.error("Password ≥ 6 chars required");
        const create = { ...form };
        delete create.logbook_user_id;
        const { data: created } = await api.post("/users", create);
        await saveLogbookLink(created.id, "");
        toast.success(`User ${form.email} created`);
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(formatApiError(e.response?.data?.detail) || "Save failed");
    } finally {
      setBusy(false);
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.email}?`)) return;
    try {
      await api.delete(`/users/${u.id}`);
      toast.success("User deleted");
      load();
    } catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="text-xs uppercase tracking-[0.18em] text-slate-500">Admin</div>
          <h1 className="text-3xl sm:text-4xl tracking-tight font-extrabold text-slate-900" style={{ fontFamily: "Manrope" }}>
            Users & Roles
          </h1>
          <p className="text-sm text-slate-500 mt-1">Create teammates and assign a role — Production Manager, Quality Inspector, Store/Inventory Manager, Sales/CRM, or Administrator. Each role sees only the sidebar sections relevant to their work.</p>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
        <HrUserImport onDone={load} />
        <Sheet open={open} onOpenChange={setOpen}>
          <SheetTrigger asChild>
            <Button onClick={startCreate} className="bg-blue-600 hover:bg-blue-700" data-testid="new-user-btn">
              <Plus className="w-4 h-4 mr-1.5" /> New User
            </Button>
          </SheetTrigger>
          <SheetContent className="w-full sm:max-w-md">
            <SheetHeader>
              <SheetTitle>{editing ? "Edit" : "New"} User</SheetTitle>
              <SheetDescription>{editing ? "Update name, role, or reset password." : "Create a teammate and assign their role."}</SheetDescription>
            </SheetHeader>
            <div className="mt-6 space-y-4">
              <div>
                <Label>Email</Label>
                <Input type="email" value={form.email} disabled={!!editing} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="user-email-input" />
              </div>
              <div>
                <Label>Full Name</Label>
                <Input value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} data-testid="user-name-input" />
              </div>
              <div>
                <Label>Role</Label>
                <Select value={form.role} onValueChange={(v) => setForm({ ...form, role: v })} disabled={isHr && !!editing}>
                  <SelectTrigger data-testid="user-role-select"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    {(isHr && editing ? roles : roleOptions).map((r) => <SelectItem key={r.code} value={r.code}>{r.label}</SelectItem>)}
                  </SelectContent>
                </Select>
              </div>
              {!(isHr && editing) && (
              <div>
                <Label>{editing ? "New Password (leave blank to keep)" : "Password"}</Label>
                <Input type="password" value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} placeholder={editing ? "••••••" : ""} data-testid="user-password-input" />
              </div>
              )}
              <div>
                <Label>Employee Code <span className="text-slate-400 text-[10px]">(optional)</span></Label>
                <Input value={form.employee_code} onChange={(e) => setForm({ ...form, employee_code: e.target.value })} placeholder="e.g. PA" data-testid="user-employee-code-input" />
                <p className="text-[11px] text-slate-400 mt-1">Printed ahead of the TO No. on Delivery Challans this user's Transfer Orders were raised against.</p>
              </div>

              <div className="pt-2 border-t border-slate-100" data-testid="user-hr-section">
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2 flex items-center gap-1.5"><IdCard className="w-3.5 h-3.5" /> HR Module</div>
                <div className="space-y-3">
                  {editing && (
                    <p className="text-xs text-slate-600">
                      HR access: <b>{hrAccess(editingUser)}</b>{" "}
                      <span className="text-slate-400">— set under <Link to="/user-permissions" className="text-blue-600 hover:underline">User Permissions → HR</Link>.</span>
                    </p>
                  )}
                  <div>
                    <Label>Logbook account</Label>
                    {lbAccounts === null ? (
                      <p className="text-[11px] text-slate-400 mt-1">Logbook can't be reached right now, so the link can't be changed here. {editingUser?.logbook_name ? `Linked to ${editingUser.logbook_name}.` : ""}</p>
                    ) : (
                      <select
                        className="w-full h-9 border border-slate-200 rounded-md px-2 text-sm bg-white"
                        value={form.logbook_user_id}
                        onChange={(e) => setForm({ ...form, logbook_user_id: e.target.value })}
                        data-testid="user-logbook-select"
                      >
                        <option value="">— Not linked (match by name) —</option>
                        {lbAccounts.map((a) => (
                          <option key={a.id} value={a.id} disabled={!!a.linked_to && a.linked_to !== form.email}>
                            {a.name}{a.linked_to && a.linked_to !== form.email ? ` (linked to ${a.linked_to})` : ""}
                          </option>
                        ))}
                      </select>
                    )}
                    <p className="text-[11px] text-slate-400 mt-1">The Logbook employee this user works as on the HR page. Left on "Not linked", the user is matched by name when they open HR (no separate Logbook sign-in).</p>
                  </div>
                </div>
              </div>

              <div className="pt-2 border-t border-slate-100">
                <div className="text-xs uppercase tracking-[0.14em] text-slate-500 mb-2">Issuer Profile (used on Transfer Orders & Invoices)</div>
                <div className="space-y-3">
                  <div>
                    <Label>Designation</Label>
                    <Input value={form.designation} onChange={(e) => setForm({ ...form, designation: e.target.value })} placeholder="Sales Manager, Production Lead, etc." data-testid="user-designation-input" />
                  </div>
                  <div>
                    <Label>Mobile Number</Label>
                    <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} placeholder="+91 98765 43210" data-testid="user-phone-input" />
                  </div>
                  <div>
                    <Label>Signature Image URL <span className="text-slate-400 text-[10px]">(optional)</span></Label>
                    <Input value={form.signature_url} onChange={(e) => setForm({ ...form, signature_url: e.target.value })} placeholder="https://...signature.png" data-testid="user-signature-input" />
                    {form.signature_url && (
                      <div className="mt-2 p-2 border border-slate-200 rounded bg-slate-50 inline-block">
                        <img src={form.signature_url} alt="signature preview" style={{ height: 40 }} />
                      </div>
                    )}
                  </div>
                </div>
              </div>
            </div>
            <SheetFooter className="mt-6">
              <Button onClick={save} disabled={busy} className="bg-blue-600 hover:bg-blue-700" data-testid="save-user-btn">
                {editing ? "Save" : "Create user"}
              </Button>
            </SheetFooter>
          </SheetContent>
        </Sheet>
        </div>
      </div>

      <div className="bg-white border border-slate-200 rounded-lg overflow-hidden">
        <table className="w-full text-sm" data-testid="users-table">
          <thead>
            <tr className="text-left text-xs uppercase tracking-[0.14em] text-slate-500 bg-slate-50">
              <th className="py-3 px-4">Name</th>
              <th className="py-3 px-4">Email</th>
              <th className="py-3 px-4">Employee Code</th>
              <th className="py-3 px-4">Designation</th>
              <th className="py-3 px-4">Role</th>
              <th className="py-3 px-4">HR · Logbook</th>
              <th className="py-3 px-4">Created</th>
              <th className="py-3 px-4 text-right">Actions</th>
            </tr>
          </thead>
          <tbody>
            {list.map((u) => (
              <tr key={u.id} className="border-t border-slate-100 hover:bg-slate-50/60" data-testid={`user-row-${u.email}`}>
                <td className="py-3 px-4 font-medium text-slate-800">{u.name}</td>
                <td className="py-3 px-4 mono text-xs text-slate-600">
                  {usesHrPassword(u) ? <>{u.hr_emp_code} <span className="font-sans text-[10px] text-slate-400">· signs in with HR password</span></> : u.email}
                </td>
                <td className="py-3 px-4 mono text-xs text-slate-600">{u.employee_code || "—"}</td>
                <td className="py-3 px-4 text-xs text-slate-600">{u.designation || "—"}</td>
                <td className="py-3 px-4">
                  <span className={`text-[11px] uppercase tracking-wider px-2 py-0.5 rounded-full ${ROLE_CLS[u.role] || "bg-slate-100 text-slate-600"}`}>
                    {(roles.find((r) => r.code === u.role)?.label || u.role).replace(/_/g, " ")}
                  </span>
                </td>
                <td className="py-3 px-4 text-xs text-slate-600" data-testid={`user-hr-${u.email}`}>
                  <div className={hrAccess(u) === "No" ? "text-slate-400" : ""}>{hrAccess(u) === "No" ? "No HR access" : "HR"}</div>
                  <div className="text-slate-400">{u.logbook_name || "—"}</div>
                </td>
                <td className="py-3 px-4 text-xs text-slate-500">
                  {u.created_at ? new Date(u.created_at).toLocaleDateString() : "—"}
                </td>
                <td className="py-3 px-4 text-right">
                  {(isAdmin || u.role !== "admin") && (
                    <Button variant="ghost" size="icon" onClick={() => startEdit(u)} data-testid={`edit-user-${u.email}`}><Pencil className="w-4 h-4 text-slate-500" /></Button>
                  )}
                  {isAdmin && u.id !== user.id && (
                    <Button variant="ghost" size="icon" onClick={() => remove(u)} data-testid={`delete-user-${u.email}`}><Trash2 className="w-4 h-4 text-red-500" /></Button>
                  )}
                </td>
              </tr>
            ))}
            {list.length === 0 && <tr><td colSpan={8} className="py-10 text-center text-slate-500 text-sm">No users yet.</td></tr>}
          </tbody>
        </table>
      </div>

      <PdiPermissionSettings />
    </div>
  );
}
